import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfigService, GridBaseQuery, GridHelperService } from '@core';
import { KendoDataSourceResultDto } from '@shared';
import { CaseResponseDto } from '../dtos/case-response.dto';
import { CaseListItemResponseDto } from '../dtos/case-list-item-response.dto';
import { DashboardCaseRowResponseDto } from '../dtos/dashboard-case-row-response.dto';
import { CaseFieldValueResponseDto } from '../dtos/case-field-value-response.dto';
import { CreateCaseRequestDto } from '../dtos/create-case-request.dto';
import { CreateEmployeeCaseRequestDto } from '../dtos/create-employee-case-request.dto';
import { CreateCaseResponseDto } from '../dtos/create-case-response.dto';
import { UpdateCaseRequestDto } from '../dtos/update-case-request.dto';
import { CaseStatusSnapshotResponseDto } from '../dtos/case-status-snapshot-response.dto';
import { CasePrioritySnapshotResponseDto } from '../dtos/case-priority-snapshot-response.dto';
import { ChecklistStepResponseDto } from '../dtos/checklist-step-response.dto';
import { UpdateChecklistStepRequestDto } from '../dtos/update-checklist-step-request.dto';
import { CaseMessageResponseDto } from '../dtos/case-message-response.dto';
import { AddMessageRequestDto } from '../dtos/add-message-request.dto';
import { WorkNoteResponseDto } from '../dtos/work-note-response.dto';
import { AddWorkNoteRequestDto } from '../dtos/add-work-note-request.dto';
import { CaseAttachmentResponseDto } from '../dtos/case-attachment-response.dto';
import { GetAttachmentUploadUrlRequestDto, GetAttachmentUploadUrlResponseDto } from '../dtos/get-attachment-upload-url.dto';
import { ConfirmAttachmentUploadRequestDto, ConfirmAttachmentUploadResponseDto } from '../dtos/confirm-attachment-upload.dto';
import { AttachmentDownloadUrlResponseDto } from '../dtos/get-attachment-download-url.dto';
import { LinkedCaseResponseDto } from '../dtos/linked-case-response.dto';
import { LinkCaseRequestDto } from '../dtos/link-case-request.dto';
import { CaseParentChildResponseDto } from '../dtos/case-parent-child-response.dto';
import { ClosingReasonResponseDto } from '../dtos/closing-reason-response.dto';
import { CloseCaseRequestDto } from '../dtos/close-case-request.dto';
import { ReopeningReasonResponseDto } from '../dtos/reopening-reason-response.dto';
import { ReopenCaseRequestDto } from '../dtos/reopen-case-request.dto';
import { UpdateCaseAssigneesRequestDto } from '../dtos/update-case-assignees-request.dto';
import { UpdateCaseStatusRequestDto } from '../dtos/update-case-status-request.dto';
import { UpdateCasePriorityRequestDto } from '../dtos/update-case-priority-request.dto';
import { EmployeeResponseDto } from '../dtos/employee-response.dto';
import { AddInvolvedEmployeesRequestDto } from '../dtos/add-involved-employees-request.dto';
import { UpdateCaseFieldValuesRequestDto } from '../dtos/update-case-field-values-request.dto';
import { TransferToErRequestDto } from '../dtos/transfer-to-er-request.dto';
import { CasePrintResponseDto } from '../dtos/case-print-response.dto';
import { CaseAssignmentOptionsResponseDto } from '../dtos/case-assignment-options-response.dto';
import { CaseAssigneeCandidateResponseDto } from '../dtos/case-assignee-candidates-response.dto';
import { CaseApprovalRequestResponseDto } from '../dtos/case-approval-request-response.dto';
import { DenyApprovalRequestDto } from '../dtos/deny-approval-request.dto';
import { CaseApprovalResponseDto } from '../dtos/case-approval-response.dto';

/**
 * Cases API service.
 * Handles HTTP communication with Case endpoints.
 */
@Injectable({ providedIn: 'root' })
export class CasesApi {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(AppConfigService);
  private readonly _gridHelper = inject(GridHelperService);
  private readonly _baseUrl = `${this._config.versionedApiUrl}/cases`;

  /**
   * Validates string ID parameter (encrypted UUID)
   * @throws Error if ID is invalid
   */
  private _validateStringId(id: string, paramName = 'ID'): void {
    if (!id || typeof id !== 'string' || id.trim().length === 0) {
      throw new Error(`Invalid ${paramName}: ${id}`);
    }
  }

  public getAllCases(): Observable<CaseResponseDto[]> {
    return this._http.get<KendoDataSourceResultDto<CaseResponseDto>>(this._baseUrl).pipe(
      map((result) => result.data),
    );
  }

  /**
   * Retrieves a list of all cases (summary data for dropdowns).
   * @returns Observable with case list items (returns all data, no pagination)
   */
  public getCasesList(): Observable<CaseListItemResponseDto[]> {
    return this._http.get<KendoDataSourceResultDto<CaseListItemResponseDto>>(this._baseUrl).pipe(
      map((result) => result.data)
    );
  }

  /**
   * Retrieves a paginated list of cases with optional filtering.
   * @param params - HTTP parameters including Skip, Take, Sort, Filter
   * @returns Observable with paginated response (data, totalElements, totalPages)
   */
  public getCasesListPaginated(params: HttpParams): Observable<KendoDataSourceResultDto<CaseListItemResponseDto>> {
    return this._http.get<KendoDataSourceResultDto<CaseListItemResponseDto>>(this._baseUrl, { params });
  }

  // Same endpoint as getCasesListPaginated but the response is mapped into the richer DashboardCaseRowResponseDto.
  public getDashboardCases(params: HttpParams): Observable<KendoDataSourceResultDto<DashboardCaseRowResponseDto>> {
    return this._http.get<KendoDataSourceResultDto<DashboardCaseRowResponseDto>>(this._baseUrl, { params });
  }

  public getCaseById(id: string): Observable<CaseResponseDto> {
    this._validateStringId(id, 'case ID');
    return this._http.get<CaseResponseDto>(`${this._baseUrl}/${id}`);
  }

  /**
   * Retrieves case data optimized for printing.
   * Single endpoint that returns case details, field values, and involved employees.
   * Includes automatic audit logging and PII redaction.
   * @param id - Encrypted case ID
   * @returns Observable with complete print data
   */
  public getCaseForPrint(id: string): Observable<CasePrintResponseDto> {
    this._validateStringId(id, 'case ID');
    return this._http.get<CasePrintResponseDto>(`${this._baseUrl}/${id}/print`);
  }

  public createCase(request: CreateCaseRequestDto): Observable<CreateCaseResponseDto> {
    return this._http.post<CreateCaseResponseDto>(this._baseUrl, request);
  }

  /**
   * Creates a case via employee portal endpoint.
   * @param request - Employee create case request
   * @returns Observable with created case response (caseId and caseNumber)
   */
  public createEmployeeCase(request: CreateEmployeeCaseRequestDto): Observable<CreateCaseResponseDto> {
    return this._http.post<CreateCaseResponseDto>(`${this._baseUrl}/employee`, request);
  }

  public updateCase(id: string, request: UpdateCaseRequestDto): Observable<{ id: string }> {
    this._validateStringId(id, 'case ID');
    return this._http.put<{ id: string }>(`${this._baseUrl}/${id}`, request);
  }

  public deleteCase(id: string): Observable<void> {
    this._validateStringId(id, 'case ID');
    return this._http.delete<void>(`${this._baseUrl}/${id}`);
  }

  public getCaseFieldValues(caseId: string): Observable<CaseFieldValueResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CaseFieldValueResponseDto[]>(`${this._baseUrl}/${caseId}/field-values`);
  }

  public getStatusSnapshots(caseId: string): Observable<CaseStatusSnapshotResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CaseStatusSnapshotResponseDto[]>(`${this._baseUrl}/${caseId}/status-snapshots`);
  }

  public getPrioritySnapshots(caseId: string): Observable<CasePrioritySnapshotResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CasePrioritySnapshotResponseDto[]>(`${this._baseUrl}/${caseId}/priority-snapshots`);
  }

  public getChecklistSteps(caseId: string): Observable<ChecklistStepResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<ChecklistStepResponseDto[]>(`${this._baseUrl}/${caseId}/checklist-steps`);
  }

  public updateChecklistStep(caseId: string, stepId: string, request: UpdateChecklistStepRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(stepId, 'step ID');
    return this._http.patch<void>(`${this._baseUrl}/${caseId}/checklist-steps/${stepId}`, request);
  }

  public getMessages(caseId: string): Observable<CaseMessageResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CaseMessageResponseDto[]>(`${this._baseUrl}/${caseId}/messages`);
  }

  public addMessage(caseId: string, request: AddMessageRequestDto): Observable<{ id: string }> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<{ id: string }>(`${this._baseUrl}/${caseId}/messages`, request);
  }

  public markMessagesAsRead(caseId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/messages/mark-read`, null);
  }

  public getWorkNotes(caseId: string): Observable<WorkNoteResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<WorkNoteResponseDto[]>(`${this._baseUrl}/${caseId}/work-notes`);
  }

  public addWorkNote(caseId: string, request: AddWorkNoteRequestDto): Observable<{ id: string }> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<{ id: string }>(`${this._baseUrl}/${caseId}/work-notes`, request);
  }

  public getAttachments(caseId: string): Observable<CaseAttachmentResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CaseAttachmentResponseDto[]>(`${this._baseUrl}/${caseId}/attachments`);
  }

  /**
   * Step 1 of 3-step upload: Get pre-signed S3 upload URL.
   * URL expires in 15 minutes.
   * @param caseId - Encrypted case ID
   * @param request - File metadata for upload
   * @returns Observable with upload URL and uploadId for Step 3
   */
  public getAttachmentUploadUrl(
    caseId: string,
    request: GetAttachmentUploadUrlRequestDto
  ): Observable<GetAttachmentUploadUrlResponseDto> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<GetAttachmentUploadUrlResponseDto>(
      `${this._baseUrl}/${caseId}/attachment-upload-url`,
      request
    );
  }

  /**
   * Step 3 of 3-step upload: Confirm upload completion to backend.
   * Called after Step 2 (direct S3 PUT using pre-signed URL from Step 1).
   * @param caseId - Encrypted case ID
   * @param request - Upload ID from Step 1
   * @returns Observable with attachment ID
   */
  public confirmAttachmentUpload(
    caseId: string,
    request: ConfirmAttachmentUploadRequestDto
  ): Observable<ConfirmAttachmentUploadResponseDto> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<ConfirmAttachmentUploadResponseDto>(
      `${this._baseUrl}/${caseId}/attachments`,
      request
    );
  }

  /**
   * Get pre-signed download URL for an attachment.
   * URL expires in 60 minutes.
   * @param caseId - Encrypted case ID
   * @param attachmentId - Encrypted attachment ID
   * @returns Observable with download URL and metadata
   */
  public getAttachmentDownloadUrl(
    caseId: string,
    attachmentId: string
  ): Observable<AttachmentDownloadUrlResponseDto> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(attachmentId, 'attachment ID');
    return this._http.get<AttachmentDownloadUrlResponseDto>(
      `${this._baseUrl}/${caseId}/attachments/${attachmentId}/download-url`
    );
  }

  /**
   * Delete an attachment.
   * Also deletes the file from S3.
   * @param caseId - Encrypted case ID
   * @param attachmentId - Encrypted attachment ID
   * @returns Observable void (204 No Content)
   */
  public deleteAttachment(caseId: string, attachmentId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(attachmentId, 'attachment ID');
    return this._http.delete<void>(`${this._baseUrl}/${caseId}/attachments/${attachmentId}`);
  }

  public getLinkedCases(caseId: string): Observable<LinkedCaseResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<LinkedCaseResponseDto[]>(`${this._baseUrl}/${caseId}/linked-cases`);
  }

  /**
   * Retrieves the parent and children for a case.
   * @param caseId - Encrypted case ID
   * @returns Observable with parent (or null) and children array
   */
  public getParentChildCases(caseId: string): Observable<CaseParentChildResponseDto> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CaseParentChildResponseDto>(`${this._baseUrl}/${caseId}/parent-child`);
  }

  /**
   * Links existing cases as children of the given case.
   * @param caseId - Encrypted case ID of the parent
   * @param childCaseIds - Array of encrypted case IDs to add as children
   * @returns Observable void (204 No Content)
   */
  public addChildCases(caseId: string, childCaseIds: string[]): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/children`, { childCaseIds });
  }

  /**
   * Sets the parent case for a case.
   * @param caseId - Encrypted case ID of the child
   * @param parentCaseId - Encrypted case ID of the parent
   * @returns Observable void (204 No Content)
   */
  public setParentCase(caseId: string, parentCaseId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(parentCaseId, 'parent case ID');
    return this._http.put<void>(`${this._baseUrl}/${caseId}/parent`, { parentCaseId });
  }

  /**
   * Removes a parent/child relationship between cases.
   * @param caseId - Encrypted case ID
   * @param linkedCaseId - Encrypted ID of the related (parent or child) case to unlink
   * @returns Observable void (204 No Content)
   */
  public unlinkParentChild(caseId: string, linkedCaseId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(linkedCaseId, 'linked case ID');
    return this._http.delete<void>(`${this._baseUrl}/${caseId}/parent-child/${linkedCaseId}`);
  }

  public linkCase(caseId: string, request: LinkCaseRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/linked-cases`, request);
  }

  public unlinkCase(caseId: string, linkedCaseId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(linkedCaseId, 'linked case ID');
    return this._http.delete<void>(`${this._baseUrl}/${caseId}/linked-cases/${linkedCaseId}`);
  }

  public updateLinkType(caseId: string, linkedCaseId: string, linkType: 'parent' | 'child' | 'related'): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(linkedCaseId, 'linked case ID');
    return this._http.put<void>(`${this._baseUrl}/${caseId}/linked-cases/${linkedCaseId}/link-type`, { newLinkType: linkType });
  }

  /**
   * Get all active closing reasons
   * @returns Observable array of closing reason DTOs
   */
  public getClosingReasons(): Observable<ClosingReasonResponseDto[]> {
    return this._http.get<ClosingReasonResponseDto[]>(`${this._baseUrl}/closing-reasons`);
  }

  /**
   * Close a case with reason and notes
   * @param caseId - Encrypted case ID
   * @param request - Close case request DTO
   * @returns Observable void (204 No Content)
   */
  public closeCase(caseId: string, request: CloseCaseRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/close`, request);
  }

  /**
   * Get all active reopening reasons
   * @returns Observable array of reopening reason DTOs
   */
  public getReopeningReasons(): Observable<ReopeningReasonResponseDto[]> {
    return this._http.get<ReopeningReasonResponseDto[]>(`${this._baseUrl}/reopening-reasons`);
  }

  /**
   * Reopen a closed case with reason and notes
   * @param caseId - Encrypted case ID
   * @param request - Reopen case request DTO
   * @returns Observable void (204 No Content)
   */
  public reopenCase(caseId: string, request: ReopenCaseRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/reopen`, request);
  }

  public updateCaseAssignees(caseId: string, request: UpdateCaseAssigneesRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.patch<void>(`${this._baseUrl}/${caseId}/assignees`, request);
  }

  public getCaseAssigneeCandidates(caseId: string): Observable<CaseAssigneeCandidateResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CaseAssigneeCandidateResponseDto[]>(`${this._baseUrl}/${caseId}/assignment-options`);
  }

  public getWatcherOptions(caseId: string): Observable<CaseAssigneeCandidateResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CaseAssigneeCandidateResponseDto[]>(`${this._baseUrl}/${caseId}/watcher-options`);
  }

  public getBulkCaseAssignmentOptions(caseIds: string[]): Observable<CaseAssigneeCandidateResponseDto[]> {
    return this._http.post<CaseAssigneeCandidateResponseDto[]>(`${this._baseUrl}/bulk-assignment-options`, { caseIds });
  }

  public getCaseTypeAssignmentOptions(caseTypeId: string, search?: string): Observable<CaseAssignmentOptionsResponseDto> {
    this._validateStringId(caseTypeId, 'case type ID');
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    return this._http.get<CaseAssignmentOptionsResponseDto>(
      `${this._baseUrl}/case-types/${caseTypeId}/assignment-options`,
      { params }
    );
  }

  public updateCaseStatus(caseId: string, request: UpdateCaseStatusRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.patch<void>(`${this._baseUrl}/${caseId}/status`, request);
  }

  public updateCasePriority(caseId: string, request: UpdateCasePriorityRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.patch<void>(`${this._baseUrl}/${caseId}/priority`, request);
  }

  public getInvolvedEmployees(caseId: string): Observable<EmployeeResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<EmployeeResponseDto[]>(`${this._baseUrl}/${caseId}/involved-employees`);
  }

  public addInvolvedEmployees(caseId: string, request: AddInvolvedEmployeesRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/involved-employees`, request);
  }

  public removeInvolvedEmployee(caseId: string, userId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(userId, 'user ID');
    return this._http.delete<void>(`${this._baseUrl}/${caseId}/involved-employees/${userId}`);
  }

  public updateCaseFieldValues(caseId: string, request: UpdateCaseFieldValuesRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.patch<void>(`${this._baseUrl}/${caseId}/field-values`, request);
  }

  /**
   * Transfers a case to Employee Relations (ER).
   * Returns 204 No Content on success.
   * @param caseId - Encrypted case UUID
   * @param request - Transfer request with closing notes
   * @returns Observable void (204 No Content)
   */
  public transferToEr(caseId: string, request: TransferToErRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/transfer-to-er`, request);
  }

  public getApprovalRequests(caseId: string): Observable<CaseApprovalRequestResponseDto[]> {
    this._validateStringId(caseId, 'case ID');
    return this._http.get<CaseApprovalRequestResponseDto[]>(`${this._baseUrl}/${caseId}/approval-requests`);
  }

  public submitForApproval(caseId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/submit-for-approval`, null);
  }

  public routeCase(caseId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/route-case`, null);
  }

  public approveRequest(caseId: string, approvalRequestId: string): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(approvalRequestId, 'approval request ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/approval-requests/${approvalRequestId}/approve`, null);
  }

  public denyRequest(caseId: string, approvalRequestId: string, body: DenyApprovalRequestDto): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    this._validateStringId(approvalRequestId, 'approval request ID');
    return this._http.post<void>(`${this._baseUrl}/${caseId}/approval-requests/${approvalRequestId}/deny`, body);
  }

  public getApprovalsWithPaging(query: GridBaseQuery): Observable<KendoDataSourceResultDto<CaseApprovalResponseDto>> {
    const params = this._gridHelper.buildGridParams(query);
    return this._http.get<KendoDataSourceResultDto<CaseApprovalResponseDto>>(`${this._baseUrl}/approvals`, { params });
  }

  /**
   * Sets watch status for a case.
   * Returns 204 No Content on success.
   * @param caseId - Encrypted case UUID
   * @param isWatching - Whether to watch (true) or unwatch (false) the case
   * @returns Observable void (204 No Content)
   */
  public setWatchCase(caseId: string, isWatching: boolean): Observable<void> {
    this._validateStringId(caseId, 'case ID');
    // Swallow 409 (already watching) and 404 on unwatch so the toggle stays idempotent if the row drifts from the server.
    const obs$ = isWatching
      ? this._http.post<void>(`${this._baseUrl}/${caseId}/watches`, {})
      : this._http.delete<void>(`${this._baseUrl}/${caseId}/watches`);
    return obs$.pipe(
      catchError((err: { status?: number }) => {
        if (err?.status === 409 || (!isWatching && err?.status === 404)) {
          return of(void 0);
        }
        return throwError(() => err);
      }),
    );
  }
}
